import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, useApi, useRefresh } from "@/lib/api";
import { toLocalInput } from "@/lib/crm";

const at = (days, hour, addHours = 0) => {
  const d = new Date();
  if (addHours) { d.setHours(d.getHours() + addHours, 0, 0, 0); return d; }
  d.setDate(d.getDate() + days);
  d.setHours(hour, 0, 0, 0);
  return d;
};
const PRESETS = [
  { l: "Tra 1 ora", d: () => at(0, 0, 1) },
  { l: "Domani 9:00", d: () => at(1, 9) },
  { l: "Tra 3 giorni", d: () => at(3, 10) },
  { l: "Tra 1 settimana", d: () => at(7, 10) },
  { l: "Tra 1 mese", d: () => at(30, 10) },
  { l: "Tra 6 mesi", d: () => at(182, 10) },
];

export const DatePresets = ({ onPick }) => (
  <div className="flex flex-wrap gap-1.5">
    {PRESETS.map((p, i) => (
      <button key={p.l} type="button" data-testid={`reminder-preset-${i}`} onClick={() => onPick(toLocalInput(p.d()))}
        className="px-2.5 py-1 rounded-full text-xs border border-slate-200 text-slate-600 hover:border-sky-500 hover:text-sky-700 transition-colors">
        {p.l}
      </button>
    ))}
  </div>
);

export const ReminderDialog = ({ open, onOpenChange, clientId, reminder, defaultDate }) => {
  const [f, setF] = useState({});
  const refresh = useRefresh();
  const { data: clients = [] } = useApi("/clients", { enabled: open && !clientId });
  useEffect(() => {
    if (!open) return;
    setF(reminder
      ? { ...reminder, due_at: toLocalInput(new Date(reminder.due_at)) }
      : { client_id: clientId || "", due_at: toLocalInput(defaultDate || at(1, 9)), reason: "", requested_by: "agente" });
  }, [open, reminder, clientId, defaultDate]);

  const save = async () => {
    if (!f.client_id) return toast.error("Seleziona un cliente");
    if (!f.due_at) return toast.error("Indica data e ora");
    const body = { client_id: f.client_id, due_at: new Date(f.due_at).toISOString(), reason: f.reason, requested_by: f.requested_by, practice_id: f.practice_id || null };
    try {
      reminder ? await api.put(`/reminders/${reminder.reminder_id}`, body) : await api.post("/reminders", body);
      toast.success("Richiamo programmato");
      refresh();
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-w-lg" data-testid="reminder-dialog">
        <DialogHeader><DialogTitle className="font-display">{reminder ? "Modifica richiamo" : "Programma richiamo"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {!clientId && (
            <div className="space-y-1.5"><Label className="text-xs text-slate-500">Cliente *</Label>
              <Select value={f.client_id} onValueChange={(v) => setF({ ...f, client_id: v })}>
                <SelectTrigger data-testid="reminder-client-select"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.client_id} value={c.client_id}>{c.first_name} {c.last_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-500">Data e ora</Label>
            <Input data-testid="reminder-datetime" type="datetime-local" value={f.due_at || ""} onChange={(e) => setF({ ...f, due_at: e.target.value })} />
            <DatePresets onPick={(v) => setF({ ...f, due_at: v })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-500">Chi ha fissato il richiamo?</Label>
            <div className="grid grid-cols-2 gap-2">
              {[["agente", "Deciso da me"], ["cliente", "Richiesto dal cliente"]].map(([id, l]) => (
                <button key={id} type="button" data-testid={`reminder-by-${id}`} onClick={() => setF({ ...f, requested_by: id })}
                  className={`rounded-lg border px-3 py-2 text-sm transition-colors ${f.requested_by === id ? "border-sky-500 bg-sky-50 text-sky-800" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-500">Motivo</Label>
            <Textarea data-testid="reminder-reason" rows={2} value={f.reason || ""} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="Es. richiamare per esito delibera, chiedere buste paga…" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button data-testid="reminder-save-btn" onClick={save} className="bg-[#0F172A] hover:bg-sky-600">Salva richiamo</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
